import type { IDropdownListAction } from '@/interfaces';
import { EIcon } from 'common/enum/misc.enum';

interface IRelationActionMenuOptions {
  competencesLabel: string;
  editCompetences: () => Promise<void> | void;
  editStudents: () => Promise<void> | void;
  studentsLabel: string;
}

interface IPerformanceAssessmentActionMenuOptions extends IRelationActionMenuOptions {
  deleteEntry: () => Promise<void> | void;
  deleteLabel: string;
  duplicateEntry: () => Promise<void> | void;
  duplicateLabel: string;
  printDisabled: boolean;
  printEntry: () => Promise<void> | void;
  printLabel: string;
}

interface ISupportMeasureActionMenuOptions extends IRelationActionMenuOptions {
  complete: () => Promise<void> | void;
  completeLabel: string;
  createReport: () => Promise<void> | void;
  createReportLabel: string;
  deleteEntry: () => Promise<void> | void;
  deleteLabel: string;
  mobile: boolean;
  readonly: boolean;
  /** Only running measures create reports from the menu; a completed one has the report button in its header. */
  showCreateReport: boolean;
}

const createRelationActions = (options: IRelationActionMenuOptions): IDropdownListAction[] => [
  {
    label: options.studentsLabel,
    action: options.editStudents,
    iconBefore: EIcon.ADD_USER,
  },
  {
    label: options.competencesLabel,
    action: options.editCompetences,
    iconBefore: EIcon.COMPETENCES,
    separatorAfter: true,
  },
];

export const createPerformanceAssessmentActionMenu = (
  options: IPerformanceAssessmentActionMenuOptions,
): IDropdownListAction[] => [
  ...createRelationActions(options),
  { label: options.duplicateLabel, action: options.duplicateEntry, iconBefore: EIcon.DUPLICATE },
  {
    label: options.printLabel,
    action: options.printEntry,
    disabled: options.printDisabled,
    iconBefore: EIcon.PRINT,
    separatorAfter: true,
  },
  { label: options.deleteLabel, action: options.deleteEntry, iconBefore: EIcon.DELETE, style: 'error' },
];

export const createSupportMeasureActionMenu = (options: ISupportMeasureActionMenuOptions): IDropdownListAction[] => {
  const deleteAction: IDropdownListAction = {
    label: options.deleteLabel,
    action: options.deleteEntry,
    iconBefore: EIcon.DELETE,
    style: 'error',
  };
  const reportActions: IDropdownListAction[] = options.showCreateReport
    ? [
        {
          label: options.createReportLabel,
          action: options.createReport,
          iconBefore: EIcon.DOWNLOAD,
          separatorAfter: true,
        },
      ]
    : [];
  if (options.readonly) return [...reportActions, deleteAction];

  const actions = [...createRelationActions(options), ...reportActions, deleteAction];
  if (!options.mobile) return actions;

  return [
    {
      label: options.completeLabel,
      action: options.complete,
      iconBefore: EIcon.CHECKMARK,
      separatorAfter: true,
    },
    ...actions,
  ];
};
